import React, {useMemo} from 'react';
import {shallowEqual} from 'react-redux';
import {useAppSelector} from 'store/customer';
import {selectApplicationLogo} from 'store/customer/brandingSlice';
import styled from 'styled-components';
import {PartialJob} from 'shared/types/PartialJob';
import {useJobContext} from 'contexts';
import {DateTime, Format} from '@cabinetsbycomputer/datetime';

const RoomPlannerPdfHeader = ({
    title,
    subTitle,
}: {
    title?: string;
    subTitle?: string;
}) => {
    const logo = useAppSelector(selectApplicationLogo, shallowEqual);
    const {job} = useJobContext() as PartialJob;
    const submittedDate = useMemo(() => {
        if (job?.dateSubmitted) {
            return DateTime.parseCustom(job?.dateSubmitted, Format.DateTime)
                .format(Format.DayMonthYearAtTimeFormat)
                .addOrdinalSuffix()
                .toString();
        }
        return '-';
    }, [job]);

    return (
        <HeaderContainer>
            <LeftGroup>
                {title ? <Title>{title}</Title> : null}
                {subTitle ? <SubTitle>{subTitle}</SubTitle> : null}
            </LeftGroup>
            <RightGroup>
                <JobInfo>
                    <Row>
                        <Label>JOB #</Label>
                        <Value>{job?.displayId ?? 'N/A'}</Value>
                    </Row>
                    <Row>
                        <Label>JOB NAME:</Label>
                        <Value>{job?.name ?? 'N/A'}</Value>
                    </Row>
                    <Row>
                        <Label>SUBMITTED:</Label>
                        <Value>{submittedDate}</Value>
                    </Row>
                </JobInfo>
                {logo ? <Logo src={logo} alt="logo" /> : null}
            </RightGroup>
        </HeaderContainer>
    );
};

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    border-bottom: 1px solid #999;
    padding-bottom: 10px;
    margin-bottom: 30px;
`;

const LeftGroup = styled.div`
    display: flex;
    flex-direction: column;
`;

const RightGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
`;

const JobInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    text-align: left;
    justify-content: center;
`;

const Title = styled.strong`
    font-size: 20px;
    font-weight: 700;
    color: #212121;
    text-transform: uppercase;
`;

const SubTitle = styled.strong`
    font-size: 20px;
    font-weight: 700;
    color: #777;
    text-transform: uppercase;
`;

const Row = styled.div`
    font-size: 14px;
    display: flex;
    justify-content: flex-start;
    gap: 8px;
`;

const Label = styled.span`
    font-weight: bold;
    color: #333;
`;

const Value = styled.span`
    color: #333;
`;

const Logo = styled.img`
    height: 50px;
    max-width: 120px;
    object-fit: contain;
`;

export default RoomPlannerPdfHeader;
